// src/pages/AverageCostCalculator.js

import React, { useMemo, useState, useEffect } from "react";

/* --------------------- helpers --------------------- */
const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
};

function numFmt(v, d = 2) {
  if (!Number.isFinite(v)) return "—";
  const neg = v < 0;
  const abs = Math.abs(v);
  const s = abs.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
  return (neg ? "-" : "") + s;
}

function decimalsFromInput(input, fallback = 2) {
  if (!input) return fallback;
  const m = String(input).match(/[.,](\d+)/);
  return m ? Math.min(8, Math.max(fallback, m[1].length)) : fallback;
}

function Segmented({ value, onChange, options, activeColors }) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 6,
        background: "#0f0f0f",
        padding: 4,
        borderRadius: 12,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
      }}
    >
      {options.map((o) => {
        const selected = value === o.value;
        const bg = selected ? activeColors?.[o.value] ?? "#212121" : "transparent";
        const color = selected ? "#fff" : "#c7c7c7";
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              fontWeight: 700,
              border: "none",
              background: bg,
              color,
              cursor: "pointer",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Card({ title, subtitle, children, right, compact = false, stretch = false }) {
  return (
    <section
      style={{
        background: "#161616",
        borderRadius: 14,
        padding: compact ? 12 : 16,
        boxShadow: "0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.05)",
        overflow: "hidden",
        display: stretch ? "flex" : undefined,
        flexDirection: stretch ? "column" : undefined,
        flex: stretch ? "1 1 auto" : undefined,
        width: stretch ? "100%" : undefined,
        minWidth: stretch ? 0 : undefined,
      }}
    >
      {(title || subtitle || right) && (
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: compact ? 6 : 10,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title && (
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", marginBottom: 2 }}>{title}</div>
            )}
            {subtitle && <div style={{ color: "#a1a1a1", fontSize: 12 }}>{subtitle}</div>}
          </div>
          {right && <div>{right}</div>}
        </header>
      )}
      <div style={{ flex: stretch ? "1 1 auto" : undefined, display: "grid", minHeight: 0 }}>
        {children}
      </div>
    </section>
  );
}

function NumberField({ label, value, onChange, rightAddon, placeholder, disabled = false }) {
  const isStringAddon = typeof rightAddon === "string";
  return (
    <label style={{ display: "grid", gap: 6, opacity: disabled ? 0.55 : 1 }}>
      <div style={{ color: "#cfcfcf", fontSize: 12, fontWeight: 600 }}>{label}</div>
      <div style={inputShellStyle()}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode="decimal"
          disabled={disabled}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#fff",
            fontSize: 14,
          }}
        />
        {rightAddon && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: isStringAddon ? "#9a9a9a" : undefined,
              fontSize: isStringAddon ? 12 : undefined,
              whiteSpace: "nowrap",
            }}
          >
            {rightAddon}
          </div>
        )}
      </div>
    </label>
  );
}

/* --------------------- goal calculators (pure) --------------------- */
function calcTargetAvg({ curQty, curAvg, targetAvgInput, assumedPrice }) {
  const q0 = curQty;
  const acp0 = curAvg;
  const tgt = Math.max(0, toNum(targetAvgInput));
  const px = Math.max(0, toNum(assumedPrice));
  const denom = px - tgt;
  if (!(Math.abs(denom) > 1e-12)) return { unsolvable: true };

  const needQty = (q0 * (tgt - acp0)) / denom;
  if (!Number.isFinite(needQty) || needQty < 0) return { unsolvable: true };

  const qty = needQty;
  const cost = qty * px; // fees handled elsewhere
  const newQty = q0 + qty;
  const newACP = newQty > 0 ? (q0 * acp0 + qty * px) / newQty : 0;
  const huge = q0 > 0 ? qty > q0 * 20 : qty > 1e9;
  return { qty, cost, newACP, unsolvable: false, huge, priceUsed: px };
}

function calcTargetPL({
  curQty,
  curAvg,
  currentPrice,
  targetPLPct,
  buyAtCurrentPrice,
  assumedPriceInput,
}) {
  const q0 = curQty;
  const a0 = curAvg;
  const pBuy = buyAtCurrentPrice ? currentPrice : Math.max(0, toNum(assumedPriceInput));
  const T = toNum(targetPLPct);

  // denom = pBuy*(1+T/100) - currentPrice
  const denom = pBuy * (1 + T / 100) - currentPrice;
  if (!(Math.abs(denom) > 1e-12)) return { unsolvable: true };

  const x = (q0 * (currentPrice - a0 * (1 + T / 100))) / denom;
  if (!Number.isFinite(x) || x < 0) return { unsolvable: true };

  const qty = x;
  const cost = qty * pBuy;
  const newQty = q0 + qty;
  const newACP = newQty > 0 ? (q0 * a0 + qty * pBuy) / newQty : 0;
  const huge = q0 > 0 ? qty > q0 * 20 : qty > 1e9;
  return { qty, cost, newACP, unsolvable: false, huge, priceUsed: pBuy };
}

/* --------------------- main --------------------- */
export default function AverageCostCalculator() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 840);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 840);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const twoCols = isMobile ? "1fr 1fr" : "1fr auto";
  const tripleCols = isMobile ? "1fr" : "1fr 1fr 1fr";

  // UI State
  const [currency, setCurrency] = useState("$");
  const [posMode, setPosMode] = useState("avg"); // 'avg' | 'basis'
  const [curQtyS, setCurQtyS] = useState("");
  const [curAvgS, setCurAvgS] = useState("");
  const [curBasisS, setCurBasisS] = useState("");
  const [currentPriceS, setCurrentPriceS] = useState("");

  const [side, setSide] = useState("buy"); // 'buy' | 'sell'

  // unified trade input mode
  const [tradeInputMode, setTradeInputMode] = useState("qty"); // 'qty'|'amount'|'targetAvg'|'targetPL'
  const [userSetTradeMode, setUserSetTradeMode] = useState(false); // track manual changes

  // qty/amount fields
  const [tradeQtyS, setTradeQtyS] = useState("");
  const [tradeAmountS, setTradeAmountS] = useState("");
  const [tradePriceS, setTradePriceS] = useState("");
  const [feesS, setFeesS] = useState("");

  // merged goal fields (were "helper")
  const [targetAvgS, setTargetAvgS] = useState("");
  const [targetPriceAssumptionS, setTargetPriceAssumptionS] = useState("");
  const [targetPLPctS, setTargetPLPctS] = useState("");
  const [buyAtCurrentPrice, setBuyAtCurrentPrice] = useState(true);

  // Persist some inputs locally (back-compat with old keys)
  useEffect(() => {
    const saved = localStorage.getItem("avgCalc");
    if (saved) {
      try {
        const s = JSON.parse(saved);
        setCurrency(s.currency ?? "$");
        setPosMode(s.posMode ?? "avg");
        setCurQtyS(s.curQtyS ?? "");
        setCurAvgS(s.curAvgS ?? "");
        setCurBasisS(s.curBasisS ?? "");
        setCurrentPriceS(s.currentPriceS ?? "");
        setSide(s.side ?? "buy");

        // Map old tradeMode/helperMode to new tradeInputMode if needed
        if (s.tradeInputMode) {
          setTradeInputMode(s.tradeInputMode);
        } else {
          const tm = s.tradeMode ?? "qty"; // old: 'qty'|'amount'
          const hm = s.helperMode ?? null; // old: 'avg'|'pl'
          if (hm === "avg") setTradeInputMode("targetAvg");
          else if (hm === "pl") setTradeInputMode("targetPL");
          else setTradeInputMode(tm);
        }

        setTradeQtyS(s.tradeQtyS ?? "");
        setTradeAmountS(s.tradeAmountS ?? "");
        setTradePriceS(s.tradePriceS ?? "");
        setFeesS(s.feesS ?? "");

        setTargetAvgS(s.targetAvgS ?? "");
        setTargetPriceAssumptionS(s.targetPriceAssumptionS ?? "");
        setTargetPLPctS(s.targetPLPctS ?? "");
        setBuyAtCurrentPrice(s.buyAtCurrentPrice ?? true);
      } catch {
        // ignore bad state
      }
    }
  }, []);

  useEffect(() => {
    // save both new and legacy keys for smooth migration
    const legacyTradeMode = tradeInputMode === "qty" || tradeInputMode === "amount" ? tradeInputMode : "qty";
    const legacyHelperMode =
      tradeInputMode === "targetAvg" ? "avg" : tradeInputMode === "targetPL" ? "pl" : undefined;

    localStorage.setItem(
      "avgCalc",
      JSON.stringify({
        currency,
        posMode,
        curQtyS,
        curAvgS,
        curBasisS,
        currentPriceS,
        side,
        // new
        tradeInputMode,
        // legacy (kept to not break older states)
        tradeMode: legacyTradeMode,
        helperMode: legacyHelperMode,

        tradeQtyS,
        tradeAmountS,
        tradePriceS,
        feesS,
        targetAvgS,
        targetPriceAssumptionS,
        targetPLPctS,
        buyAtCurrentPrice,
      })
    );
  }, [
    currency,
    posMode,
    curQtyS,
    curAvgS,
    curBasisS,
    currentPriceS,
    side,
    tradeInputMode,
    tradeQtyS,
    tradeAmountS,
    tradePriceS,
    feesS,
    targetAvgS,
    targetPriceAssumptionS,
    targetPLPctS,
    buyAtCurrentPrice,
  ]);

  // Parsed current position
  const curQty = Math.max(0, toNum(curQtyS));
  const curBasis = posMode === "basis" ? Math.max(0, toNum(curBasisS)) : Math.max(0, curQty * toNum(curAvgS));
  const curAvg = curQty > 0 ? curBasis / curQty : Math.max(0, toNum(curAvgS));
  const currentPrice = toNum(currentPriceS);

  // Adaptive decimals
  const acpDecimals = Math.max(2, decimalsFromInput(posMode === "avg" ? curAvgS : String(curAvg || ""), 2));

  /* --------------------- normalized planned trade --------------------- */
  const rawPrice = Math.max(0, toNum(tradePriceS));
  const rawFees = Math.max(0, toNum(feesS));

  const normalized = useMemo(() => {
    // Default shape
    const base = {
      tradeQty: 0,
      tradeNet: 0,
      priceUsed: 0,
      warnings: {},
      derived: {},
    };

    // SELL does not support goal modes (disable gracefully)
    const goalDisabled = side === "sell" && (tradeInputMode === "targetAvg" || tradeInputMode === "targetPL");

    if (tradeInputMode === "qty") {
      const qty = Math.max(0, toNum(tradeQtyS));
      if (side === "buy") {
        const gross = qty * rawPrice;
        const net = gross + rawFees;
        return { ...base, tradeQty: qty, tradeNet: net, priceUsed: rawPrice };
      } else {
        const gross = qty * rawPrice;
        const net = Math.max(0, gross - rawFees);
        return { ...base, tradeQty: qty, tradeNet: net, priceUsed: rawPrice };
      }
    }

    if (tradeInputMode === "amount") {
      const amt = Math.max(0, toNum(tradeAmountS));
      if (side === "buy") {
        const netCash = Math.max(0, amt);
        const cashToAsset = Math.max(0, netCash - rawFees);
        const qty = rawPrice > 0 ? cashToAsset / rawPrice : 0;
        return { ...base, tradeQty: qty, tradeNet: netCash, priceUsed: rawPrice };
      } else {
        const netCash = Math.max(0, amt);
        const grossFromSale = netCash + rawFees;
        const qty = rawPrice > 0 ? grossFromSale / rawPrice : 0;
        return { ...base, tradeQty: qty, tradeNet: netCash, priceUsed: rawPrice };
      }
    }

    // Goal modes (buy only)
    if (goalDisabled) {
      return {
        ...base,
        warnings: { unsolvable: true },
      };
    }

    if (tradeInputMode === "targetAvg") {
      const r = calcTargetAvg({
        curQty,
        curAvg,
        targetAvgInput: targetAvgS,
        assumedPrice: targetPriceAssumptionS,
      });
      if (r.unsolvable) return { ...base, warnings: { unsolvable: true } };
      const tradeQty = Math.max(0, r.qty);
      const priceUsed = r.priceUsed ?? 0;
      const tradeNet = tradeQty * priceUsed + rawFees; // fees included in "you pay"
      return {
        ...base,
        tradeQty,
        tradeNet,
        priceUsed,
        warnings: { huge: r.huge },
        derived: { qtyForGoal: r.qty, cashNeeded: r.cost, resultingAvg: r.newACP },
      };
    }

    if (tradeInputMode === "targetPL") {
      const r = calcTargetPL({
        curQty,
        curAvg,
        currentPrice,
        targetPLPct: targetPLPctS,
        buyAtCurrentPrice,
        assumedPriceInput: targetPriceAssumptionS,
      });
      if (r.unsolvable) return { ...base, warnings: { unsolvable: true } };
      const tradeQty = Math.max(0, r.qty);
      const priceUsed = r.priceUsed ?? 0;
      const tradeNet = tradeQty * priceUsed + rawFees;
      return {
        ...base,
        tradeQty,
        tradeNet,
        priceUsed,
        warnings: { huge: r.huge },
        derived: { qtyForGoal: r.qty, cashNeeded: r.cost, resultingAvg: r.newACP },
      };
    }

    return base;
  }, [
    tradeInputMode,
    side,
    tradeQtyS,
    tradeAmountS,
    rawPrice,
    rawFees,
    curQty,
    curAvg,
    targetAvgS,
    targetPriceAssumptionS,
    currentPrice,
    targetPLPctS,
    buyAtCurrentPrice,
  ]);

  /* --------------------- result of applying normalized trade --------------------- */
  const result = useMemo(() => {
    const qty = normalized.tradeQty;
    const price = normalized.priceUsed;
    const fees = rawFees;

    if (side === "buy") {
      const newQty = curQty + qty;
      const addCost = qty * price + fees;
      const totalInvested = curBasis + addCost;
      const newAvg = newQty > 0 ? totalInvested / newQty : 0;
      return {
        mode: "buy",
        newQty,
        addCost,
        totalInvested,
        newAvg,
        youPay: normalized.tradeNet,
      };
    } else {
      // Sell path: clamp sell to position for portfolio effects
      const sellQty = Math.min(curQty, qty);
      const proceedsGross = sellQty * price;
      const youReceive = Math.max(0, proceedsGross - fees);
      const realizedPL = sellQty * (price - curAvg) - fees;
      return {
        mode: "sell",
        clamped: qty > curQty,
        sellQty,
        youReceive,
        realizedPL,
        newQty: curQty - sellQty,
        totalInvested: Math.max(0, curBasis - sellQty * curAvg),
      };
    }
  }, [normalized.tradeQty, normalized.tradeNet, normalized.priceUsed, side, curQty, curBasis, curAvg, rawFees]);

  const deltas = useMemo(() => {
    if (result.mode === "buy") {
      return {
        qty: result.newQty - curQty,
        invested: result.totalInvested - curBasis,
        avg: result.newAvg - curAvg,
      };
    } else {
      return {
        qty: result.newQty - curQty,
        invested: result.totalInvested - curBasis,
        avg: 0,
      };
    }
  }, [result, curQty, curAvg, curBasis]);

  /* --------------------- current P/L (before trade) --------------------- */
  const currentPL = useMemo(() => {
    const valueNow = curQty * currentPrice;
    const pl = valueNow - curBasis;
    const pct = curBasis > 0 ? (pl / curBasis) * 100 : 0;
    return { valueNow, pl, pct };
  }, [curQty, curBasis, currentPrice]);

  /* -------- auto-default: switch to Target P/L % if current P/L is negative ----- */
  useEffect(() => {
    if (!userSetTradeMode && currentPL.pct < 0 && side === "buy" && tradeInputMode !== "targetPL") {
      setTradeInputMode("targetPL");
    }
  }, [currentPL.pct, side, tradeInputMode, userSetTradeMode]);

  /* --------------------- break-even (after trade) --------------------- */
  const breakEven = useMemo(() => {
    if (result.mode === "buy") return result.newAvg || 0;
    return curAvg;
  }, [result, curAvg]);

  const breakEvenMove = useMemo(() => {
    const qtyAfter = result.newQty ?? 0;
    if (!(currentPrice > 0) || !(qtyAfter > 0) || !(breakEven > 0)) {
      return { pct: null, dir: null };
    }
    const pct = ((breakEven / currentPrice) - 1) * 100;
    const dir = pct === 0 ? "flat" : pct > 0 ? "rise" : "fall";
    return { pct, dir };
  }, [currentPrice, breakEven, result.newQty]);

  /* P/L after applying the planned trade (valued at currentPrice) */
  const afterPL = useMemo(() => {
    const qty = result.newQty ?? 0;
    const invested = result.totalInvested ?? 0;
    const valueNow = qty * currentPrice;
    const pl = valueNow - invested;
    const pct = invested > 0 ? (pl / invested) * 100 : null;
    return { valueNow, invested, pl, pct };
  }, [result.newQty, result.totalInvested, currentPrice]);

  /* --------------------- quick targets (for PL goal) --------------------- */
  const quickPLTargets = useMemo(() => {
    const nowPct = currentPL.pct;
    if (buyAtCurrentPrice && nowPct < 0) return ["Break even", "-10%", "-25%", "-50%"];
    if (nowPct >= 0) return ["Break even", "+10%", "+25%", "+50%"];
    return ["Break even", "-25%", "+25%", "+50%"];
  }, [currentPL.pct, buyAtCurrentPrice]);

  // helper: treat any explicit user change to the trade mode as manual
  const setTradeInputModeUser = (v) => {
    setUserSetTradeMode(true);
    setTradeInputMode(v);
  };

  /* --------------------- UI --------------------- */
  return (
    <div style={{ padding: isMobile ? 12 : 20, color: "#eaeaea" }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gap: 20,
        }}
      >
        <div style={{ marginLeft: "auto" }}>
          <Segmented
            value={currency}
            onChange={setCurrency}
            options={[
              { value: "$", label: "USD $" },
              { value: "€", label: "EUR €" },
              { value: "£", label: "GBP £" },
            ]}
          />
        </div>

        {/* Row 1: Current position */}
        <div>
          <Card
            title="Current position"
            right={
              <Segmented
                value={posMode}
                onChange={setPosMode}
                options={[
                  { value: "avg", label: "Avg price" },
                  { value: "basis", label: "Cost basis" },
                ]}
              />
            }
            compact={isMobile}
          >
            <div style={{ display: "grid", gridTemplateColumns: tripleCols, gap: 10 }}>
              <NumberField label="Quantity" value={curQtyS} onChange={setCurQtyS} />
              {posMode === "avg" ? (
                <NumberField label="Average cost (input)" value={curAvgS} onChange={setCurAvgS} rightAddon={currency} />
              ) : (
                <NumberField label="Cost basis (input)" value={curBasisS} onChange={setCurBasisS} rightAddon={currency} />
              )}
              <NumberField label="Current price" value={currentPriceS} onChange={setCurrentPriceS} rightAddon={currency} />
            </div>

            {/* Summary always visible */}
            <div style={{ display: "grid", gridTemplateColumns: twoCols, gap: 8, marginTop: 10 }}>
              <div>Original average price</div>
              <div style={{ textAlign: "right" }}>{currency}{numFmt(curAvg, acpDecimals)}</div>
              <div>Original cost basis</div>
              <div style={{ textAlign: "right" }}>{currency}{numFmt(curBasis, acpDecimals)}</div>
              <div>Position value now</div>
              <div style={{ textAlign: "right" }}>{currency}{numFmt(currentPL.valueNow, acpDecimals)}</div>
              <div>Unrealized P/L</div>
              <div style={{ textAlign: "right", color: currentPL.pl >= 0 ? "#6bdc6b" : "#ff7777" }}>
                {currency}{numFmt(currentPL.pl, acpDecimals)} ({numFmt(currentPL.pct, 2)}%)
              </div>
            </div>
          </Card>
        </div>

        {/* Row 2: Planned trade (merged with helper) */}
        <div>
          <Card
            title="Planned trade"
            right={
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Segmented
                  value={side}
                  onChange={setSide}
                  options={[
                    { value: "buy", label: "Buy" },
                    { value: "sell", label: "Sell" },
                  ]}
                  activeColors={{
                    buy: "#244b2a",  // green
                    sell: "#4b2424", // red
                  }}
                />
                <Segmented
                  value={tradeInputMode}
                  onChange={setTradeInputModeUser}
                  options={[
                    { value: "qty", label: "Qty" },
                    { value: "amount", label: "Amount" },
                    { value: "targetAvg", label: "Target avg" },
                    { value: "targetPL", label: "Target P/L %" },
                  ]}
                />
              </div>
            }
            compact={isMobile}
          >
            {/* Sub-forms per mode */}
            {tradeInputMode === "qty" && (
              <div style={{ display: "grid", gridTemplateColumns: tripleCols, gap: 10 }}>
                <NumberField label="Quantity" value={tradeQtyS} onChange={setTradeQtyS} />
                <NumberField
                  label="Price"
                  value={tradePriceS}
                  onChange={setTradePriceS}
                  rightAddon={
                    <>
                      <span style={{ color: "#9a9a9a", fontSize: 12, marginRight: 6 }}>{currency}</span>
                      <button
                        type="button"
                        onClick={() => setTradePriceS(String(currentPrice))}
                        style={addonBtnStyle()}
                        title="Set to current price"
                      >
                        set current
                      </button>
                    </>
                  }
                />
                <NumberField label="Fees / commissions" value={feesS} onChange={setFeesS} rightAddon={currency} />
              </div>
            )}

            {tradeInputMode === "amount" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: tripleCols, gap: 10 }}>
                  <NumberField
                    label={side === "buy" ? "Amount to invest (total)" : "Amount to receive (net)"}
                    value={tradeAmountS}
                    onChange={setTradeAmountS}
                    rightAddon={currency}
                  />
                  <NumberField
                    label="Price"
                    value={tradePriceS}
                    onChange={setTradePriceS}
                    rightAddon={
                      <>
                        <span style={{ color: "#9a9a9a", fontSize: 12, marginRight: 6 }}>{currency}</span>
                        <button
                          type="button"
                          onClick={() => setTradePriceS(String(currentPrice))}
                          style={addonBtnStyle()}
                          title="Set to current price"
                        >
                          set current
                        </button>
                      </>
                    }
                  />
                  <NumberField label="Fees / commissions" value={feesS} onChange={setFeesS} rightAddon={currency} />
                </div>

                {/* Quick amount presets */}
                {side === "buy" && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                    {["50", "100", "250", "500"].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => {
                          setTradeInputModeUser("amount");
                          setSide("buy");
                          setTradeAmountS(amt);
                        }}
                        style={quickBtnStyle()}
                        title={`Invest ${currency}${amt}`}
                      >
                        Invest {currency}{amt}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {tradeInputMode === "targetAvg" && (
              <>
                {side === "sell" ? (
                  <div style={{ marginTop: 6, color: "#ffb74d" }}>
                    Target average is only meaningful for buys. Switch side to <b>Buy</b> to use this mode.
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: twoCols, gap: 10 }}>
                      <NumberField label="Target average" value={targetAvgS} onChange={setTargetAvgS} rightAddon={currency} />
                      <NumberField
                        label="Assumed buy price"
                        value={targetPriceAssumptionS}
                        onChange={setTargetPriceAssumptionS}
                        rightAddon={
                          <button
                            type="button"
                            onClick={() => setTargetPriceAssumptionS(String(currentPrice))}
                            style={addonBtnStyle()}
                            title="Set to current price"
                          >
                            set current
                          </button>
                        }
                      />
                    </div>

                    {normalized.warnings?.unsolvable && (
                      <div style={{ color: "#ffb74d", marginTop: 8 }}>
                        Unsolvable with these inputs (check target & prices).
                      </div>
                    )}
                    {normalized.warnings?.huge && !normalized.warnings?.unsolvable && (
                      <div style={{ color: "#ffa726", marginTop: 8 }}>
                        This requires a very large purchase relative to your current position.
                      </div>
                    )}

                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: twoCols, gap: 8 }}>
                      <div>Qty to buy @ assumed price</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.qtyForGoal) ? numFmt(normalized.derived.qtyForGoal, 8) : "—"}
                      </div>
                      <div>Cash needed</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.cashNeeded)
                          ? `${currency}${numFmt(normalized.derived.cashNeeded, acpDecimals)}`
                          : "—"}
                      </div>
                      <div>Resulting average cost</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.resultingAvg)
                          ? `${currency}${numFmt(normalized.derived.resultingAvg, acpDecimals)}`
                          : "—"}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {tradeInputMode === "targetPL" && (
              <>
                {side === "sell" ? (
                  <div style={{ marginTop: 6, color: "#ffb74d" }}>
                    Target P/L % helper applies to buys. Switch side to <b>Buy</b> to use this mode.
                  </div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                      <NumberField label="Target P/L (%)" value={targetPLPctS} onChange={setTargetPLPctS} />
                      {/* Checkbox wrapped in an input-like shell for perfect alignment */}
                      <label style={{ display: "grid", gap: 6 }}>
                        {/* keep a hidden label to match the grid spacing of NumberField */}
                        <div style={{ color: "#cfcfcf", fontSize: 12, fontWeight: 600, visibility: "hidden" }}>
                          Assume buy at current price
                        </div>
                        <div style={{ display: "flex", alignItems: "center", height: INPUT_HEIGHT }}>
                          <input
                            type="checkbox"
                            checked={buyAtCurrentPrice}
                            onChange={(e) => setBuyAtCurrentPrice(e.target.checked)}
                            style={{ margin: 0 }}
                          />
                          <span style={{ fontSize: 12, color: "#cfcfcf", marginLeft: 8 }}>
                            Assume buy at current price
                          </span>
                        </div>
                      </label>
                    </div>

                    {!buyAtCurrentPrice && (
                      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
                        <NumberField
                          label="Assumed buy price"
                          value={targetPriceAssumptionS}
                          onChange={setTargetPriceAssumptionS}
                          rightAddon={
                            <button
                              type="button"
                              onClick={() => setTargetPriceAssumptionS(String(currentPrice))}
                              style={addonBtnStyle()}
                              title="Set to current price"
                            >
                              set current
                            </button>
                          }
                        />
                      </div>
                    )}

                    {/* Dynamic quick actions */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                      {quickPLTargets.map((lab) => (
                        <button
                          key={lab}
                          onClick={() => {
                            if (lab === "Break even") setTargetPLPctS("0");
                            else setTargetPLPctS(lab.replace("%", "").replace("+", ""));
                          }}
                          style={quickBtnStyle()}
                          title={`Set target P/L to ${lab}`}
                        >
                          {lab}
                        </button>
                      ))}
                    </div>

                    {normalized.warnings?.unsolvable && (
                      <div style={{ color: "#ffb74d", marginTop: 8 }}>
                        Unsolvable with these inputs (check target & prices).
                      </div>
                    )}
                    {normalized.warnings?.huge && !normalized.warnings?.unsolvable && (
                      <div style={{ color: "#ffa726", marginTop: 8 }}>
                        This requires a very large purchase relative to your current position.
                      </div>
                    )}

                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: twoCols, gap: 8 }}>
                      <div>Qty to buy @ assumed price</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.qtyForGoal) ? numFmt(normalized.derived.qtyForGoal, 8) : "—"}
                      </div>
                      <div>Cash needed</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.cashNeeded)
                          ? `${currency}${numFmt(normalized.derived.cashNeeded, acpDecimals)}`
                          : "—"}
                      </div>
                      <div>Resulting average cost</div>
                      <div style={{ textAlign: "right" }}>
                        {Number.isFinite(normalized.derived?.resultingAvg)
                          ? `${currency}${numFmt(normalized.derived.resultingAvg, acpDecimals)}`
                          : "—"}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Footer summary of cash flow */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, gap: 12, flexWrap: "wrap" }}>
              <div style={{ marginLeft: "auto", display: "grid", gridAutoFlow: "column", gap: 12 }}>
                <div style={{ color: "#9a9a9a", fontSize: 12 }}>You {side === "buy" ? "pay" : "receive"}</div>
                <div style={{ fontWeight: 800 }}>
                  {currency}{numFmt(normalized.tradeNet, acpDecimals)}
                </div>
              </div>
            </div>

            {side === "sell" && result.clamped && (
              <div style={{ marginTop: 8, color: "#ffb74d" }}>
                Selling more than you hold — limited to your current quantity.
              </div>
            )}
          </Card>
        </div>

        {/* Row 3: Trade impact + Break-even (equal height) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 16,
            alignItems: "stretch",
          }}
        >
          {/* Trade impact */}
          <div style={{ display: "flex", minHeight: 0 }}>
            <Card
              title="Trade impact"
              subtitle={side === "buy" ? "Weighted average (fees included)" : "Average stays; sale realizes P/L"}
              compact={isMobile}
              stretch
            >
              {side === "buy" ? (
                <div style={{ display: "grid", gridTemplateColumns: twoCols, gap: 8 }}>
                  <div>
                    New quantity
                    <div style={{ color: "#9a9a9a", fontSize: 12 }}>Δ {numFmt(deltas.qty, 8)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>{numFmt(result.newQty, 8)}</div>

                  <div>
                    New average cost
                    <div style={{ color: "#9a9a9a", fontSize: 12 }}>
                      Δ {deltas.avg >= 0 ? "+" : ""}
                      {currency}{numFmt(deltas.avg, acpDecimals)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>{currency}{numFmt(result.newAvg, acpDecimals)}</div>

                  <div>
                    Total invested
                    <div style={{ color: "#9a9a9a", fontSize: 12 }}>
                      Δ {currency}{numFmt(deltas.invested, acpDecimals)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>{currency}{numFmt(result.totalInvested, acpDecimals)}</div>

                  <div>Added cost (incl. fees)</div>
                  <div style={{ textAlign: "right" }}>{currency}{numFmt(result.addCost, acpDecimals)}</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: twoCols, gap: 8 }}>
                  <div>Quantity sold</div>
                  <div style={{ textAlign: "right" }}>{numFmt(result.sellQty, 8)}</div>

                  <div>Remaining quantity</div>
                  <div style={{ textAlign: "right" }}>{numFmt(result.newQty, 8)}</div>

                  <div>Remaining cost basis</div>
                  <div style={{ textAlign: "right" }}>{currency}{numFmt(result.totalInvested, acpDecimals)}</div>

                  <div>You receive (net)</div>
                  <div style={{ textAlign: "right" }}>{currency}{numFmt(result.youReceive, acpDecimals)}</div>

                  <div>Realized P/L (this sale)</div>
                  <div
                    style={{
                      textAlign: "right",
                      color: result.realizedPL >= 0 ? "#6bdc6b" : "#ff7777",
                      fontWeight: 700,
                    }}
                  >
                    {currency}{numFmt(result.realizedPL, acpDecimals)}
                  </div>
                </div>
              )}
              <div style={{ color: "#9a9a9a", marginTop: 8 }}>
                {side === "buy"
                  ? "newAvg = (oldCost + qty×price + fees) / (oldQty + qty)"
                  : "Selling does not change per-unit average; it realizes P/L and reduces quantity."}
              </div>
            </Card>
          </div>

          {/* Break-even — centered with divider + labeled percentages */}
          <div style={{ display: "flex", minHeight: 0 }}>
            <Card title="Break-even" subtitle="After this trade" compact={isMobile} stretch>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 16,
                  textAlign: "center",
                  minHeight: 0,
                }}
              >
                {/* Row 1: Break-even price (big) */}
                <div>
                  <div style={{ color: "#cfcfcf", fontSize: isMobile ? 13 : 14, marginBottom: 4 }}>
                    Break-even price
                  </div>
                  <div style={{ fontWeight: 900, fontSize: isMobile ? 24 : 28, letterSpacing: 0.2 }}>
                    {currency}{numFmt(breakEven, acpDecimals)}
                  </div>
                </div>

                {/* Divider */}
                <div
                  style={{
                    width: "100%",
                    height: 1,
                    background: "rgba(255,255,255,0.08)",
                    margin: isMobile ? "4px 0" : "8px 0",
                  }}
                />

                {/* Row 2: Two labeled pills */}
                <div
                  style={{
                    display: "flex",
                    gap: 18,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Move needed to break even */}
                  <div style={{ minWidth: 180 }}>
                    <div style={{ color: "#cfcfcf", fontSize: 12, marginBottom: 6 }}>
                      Move to break-even
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        fontWeight: 800,
                        fontSize: isMobile ? 14 : 16,
                        padding: "6px 10px",
                        borderRadius: 10,
                        background:
                          breakEvenMove.pct == null
                            ? "#2a2a2a"
                            : breakEvenMove.dir === "rise"
                            ? "#244b2a"
                            : breakEvenMove.dir === "fall"
                            ? "#4b2424"
                            : "#2a2a2a",
                        border: "1px solid #3a3a3a",
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                      title={
                        breakEvenMove.pct == null
                          ? "Enter current price and keep some quantity to compute"
                          : "Required move from current price to reach break-even"
                      }
                    >
                      {breakEvenMove.pct == null
                        ? "—"
                        : `${Math.abs(breakEvenMove.pct).toFixed(2)}% ${breakEvenMove.dir}`}
                    </span>
                  </div>

                  {/* Unrealized P/L after trade */}
                  <div style={{ minWidth: 180 }}>
                    <div style={{ color: "#cfcfcf", fontSize: 12, marginBottom: 6 }}>
                      P/L after trade
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        fontWeight: 800,
                        fontSize: isMobile ? 14 : 16,
                        padding: "6px 10px",
                        borderRadius: 10,
                        background:
                          afterPL.pct == null
                            ? "#2a2a2a"
                            : afterPL.pct >= 0
                            ? "#244b2a"
                            : "#4b2424",
                        border: "1px solid #3a3a3a",
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                      title="Unrealized P/L percentage after applying this trade (valued at current price)"
                    >
                      {afterPL.pct == null ? "—" : `${afterPL.pct >= 0 ? "+" : ""}${numFmt(afterPL.pct, 2)}%`}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Notes */}
        <details style={{ color: "#a8a8a8" }}>
          <summary style={{ cursor: "pointer" }}>Notes</summary>
          <ul>
            <li>Current position: enter either average cost or cost basis (toggle in the card). Summary always shows both.</li>
            <li>Planned trade: choose input mode — Qty, Amount, Target avg, or Target P/L % — all in one place.</li>
            <li>“You pay / You receive” includes fees.</li>
            <li>Average-cost model on buys: fees increase cost basis; newAvg = (oldCost + qty×price + fees) / (oldQty + qty).</li>
            <li>Sells keep per-unit average cost; they realize P/L and reduce quantity.</li>
            <li>Target modes are for buys; switch to Buy to enable them.</li>
            <li>Not financial advice.</li>
          </ul>
        </details>
      </div>
    </div>
  );
}


/* --------------------- styles --------------------- */
const INPUT_HEIGHT = 20;

// compact inline button for input addons ("set current") — keeps row height consistent
function addonBtnStyle() {
  return {
    border: "none",
    background: "#1f1f1f",
    borderRadius: 8,
    padding: "2px 8px",
    color: "#eaeaea",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.1,
  };
}

// shared shell style used by NumberField and the checkbox wrapper
function inputShellStyle() {
  return {
    display: "flex",
    alignItems: "center",
    background: "#0f0f0f",
    borderRadius: 10,
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
    padding: "10px 12px",     // was 8px 10px
    gap: 8,
    height: INPUT_HEIGHT,     // NEW: hard-sets a uniform field height
  };
}

function quickBtnStyle() {
  return {
    border: "none",
    background: "#1f1f1f",
    borderRadius: 10,
    padding: "8px 10px",
    color: "#eaeaea",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
    cursor: "pointer",
    fontWeight: 700,
  };
}
